import React from 'react';
import { useStyletron } from 'baseui';
import { Panel, StatelessAccordion } from 'baseui/accordion';
import { KIND, SIZE, Tag } from 'baseui/tag';
import { ParagraphSmall } from 'baseui/typography';
import { useMountEffect } from '../hooks/use-mount-effect';
import useReviewSuggestionsHandlers from './hooks/use-review-suggestions-handlers';
import { SUGGESTION_CATEGORY_TO_COLOR_MAP, SUGGESTION_CATEGORY_TO_TITLE_MAP } from './constants';
import { ReviewSuggestionsList as ReviewSuggestionsListItemCard } from './review-suggestions-list';
import { accordionOverrides, getPanelOverrides, PanelTitleContainer, StyledCardParagraphSmall } from './styled-components-overrides';
import type { onFocusSuggestionFunctionT, ReviewSuggestionsDetailT, SuggestionActionT } from './types';
import { SUGGESTION_CATEGORY, SUGGESTION_STATE } from './types';
type PropsT = {
  data: ReviewSuggestionsDetailT[];
  onSuggestionAction: (error: ReviewSuggestionsDetailT, action: SuggestionActionT) => Promise<void>;
  onFocusSuggestion?: onFocusSuggestionFunctionT;
};
export const ReviewSuggestionsCard = ({
  data,
  onFocusSuggestion,
  onSuggestionAction
}: PropsT) => {
  const [, theme] = useStyletron();
  const getInitialExpandedPanel = () => {
    const firstNonStaleSuggestion = data.find(suggestion => suggestion.state !== SUGGESTION_STATE.STALE);
    return firstNonStaleSuggestion?.id || '';
  };
  const {
    handleFocusSuggestion
  } = useReviewSuggestionsHandlers({
    data,
    onFocusSuggestion
  });
  const [expanded, setExpanded] = React.useState<React.Key[]>(() => [getInitialExpandedPanel()]);
  const [expandedPanelIds, setExpandedPanelIds] = React.useState<React.Key[]>(() => [getInitialExpandedPanel()]); // this is used to track the expanded panel ids to change the UI of the panel

  const getSuggestionPreview = (suggestion: ReviewSuggestionsDetailT) => {
    switch (suggestion.category) {
      case SUGGESTION_CATEGORY.SPELLING_AND_GRAMMAR:
      case SUGGESTION_CATEGORY.SOP_RECOMMENDATIONS:
        return suggestion.answer;
      case SUGGESTION_CATEGORY.TRACKING_INCONSISTENCY:
        return suggestion.annotationId;
    }
  };
  // Close expanded panel if the suggestion becomes stale
  React.useEffect(() => {
    const expandedSuggestion = data.find(suggestion => suggestion.id === expanded[0]);
    if (expandedSuggestion && expandedSuggestion.state === SUGGESTION_STATE.STALE) {
      // Find the first non-stale suggestion to expand instead
      const firstNonStaleSuggestion = data.find(suggestion => suggestion.state !== SUGGESTION_STATE.STALE);
      setExpanded(firstNonStaleSuggestion ? [firstNonStaleSuggestion.id] : []);
    }
  }, [data, expanded]);
  useMountEffect(() => {
    if (data.length > 0) {
      const firstSuggestionId = getInitialExpandedPanel();
      handleFocusSuggestion(firstSuggestionId);
    }
  });
  const suggestionActionHandler = async (suggestion: ReviewSuggestionsDetailT, action: SuggestionActionT) => {
    const currentSuggestionIndex = data.findIndex(s => s.id === suggestion.id);
    await onSuggestionAction(suggestion, action);
    const nextSuggestion = data[currentSuggestionIndex + 1];
    const previousSuggestion = data[currentSuggestionIndex - 1];
    const suggestionToExpand = nextSuggestion || previousSuggestion;
    if (suggestionToExpand) {
      setExpanded([suggestionToExpand.id]);
      setExpandedPanelIds(prev => [...prev, suggestionToExpand.id]);
      handleFocusSuggestion(suggestionToExpand.id, suggestionToExpand);
    }
  };
  return <StatelessAccordion accordion expanded={(expanded as React.Key[])} overrides={accordionOverrides} onChange={({
    expanded
  }) => {
    setExpanded(expanded);
    if (expanded.length > 0) {
      handleFocusSuggestion((expanded[0] as string));
    }
    setExpandedPanelIds(prev => [...prev, ...(expanded && expanded.length > 0 && expanded[0] !== undefined ? [expanded[0]] : [])]);
  }}>
      {data.map(suggestion => {
      const suggestionCategory = SUGGESTION_CATEGORY_TO_TITLE_MAP[suggestion.category];
      const tagKind = expandedPanelIds.includes(suggestion.id) ? SUGGESTION_CATEGORY_TO_COLOR_MAP[suggestion.category] : KIND.primary;
      const isStaleSuggestion = suggestion.state === SUGGESTION_STATE.STALE;
      const isExpanded = suggestion.id === expanded[0];
      const shouldHidePreviewContent = ![SUGGESTION_CATEGORY.SPELLING_AND_GRAMMAR, SUGGESTION_CATEGORY.TRACKING_INCONSISTENCY].includes(suggestion.category) || isExpanded;
      return <Panel disabled={isStaleSuggestion} key={suggestion.id} overrides={getPanelOverrides(isStaleSuggestion)} title={<PanelTitleContainer>
                <Tag kind={tagKind} closeable={false} size={SIZE.small} overrides={{
          Text: {
            style: ({
              $theme
            }) => ({
              fontSize: $theme.sizing.scale500
            })
          }
        }} disabled={isStaleSuggestion} contentMaxWidth={theme.sizing.scale4800}>
                  {suggestionCategory}
                </Tag>
                {!shouldHidePreviewContent && <>
                    <ParagraphSmall color={isStaleSuggestion ? theme.colors.contentStateDisabled : theme.colors.contentSecondary} paddingLeft={theme.sizing.scale100} paddingRight={theme.sizing.scale100} data-testid="title-separator">
                      &#183;
                    </ParagraphSmall>
                    <StyledCardParagraphSmall color={isStaleSuggestion ? theme.colors.contentStateDisabled : theme.colors.contentSecondary}>
                      {getSuggestionPreview(suggestion)}
                    </StyledCardParagraphSmall>
                  </>}
              </PanelTitleContainer>}>
            <ReviewSuggestionsListItemCard key={suggestion.id} error={suggestion} onSuggestionAction={suggestionActionHandler} />
          </Panel>;
    })}
    </StatelessAccordion>;
};